import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Crown, LayoutDashboard, Users, Gamepad2, Settings, LogOut, 
  DollarSign, MessageCircle, ChevronRight, Search, Bell, Menu,
  Building2, Shield, ChevronDown, BarChart3, FileText, Server, Trophy, Bot, Star, Activity, Zap, PieChart, Palette, Layers,
  Wallet, Megaphone, Tag, Download, Mail, FlaskConical, Phone, Sparkles, Cloud, CreditCard, Bug, ScrollText, Disc3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

// Import Admin Pages
import DashboardPage from './DashboardPage';
import PlayersPage from './PlayersPage';
import TransactionsPage from './TransactionsPage';
import GamesPage from './GamesPage';
import ProvidersPage from './ProvidersPage';
import PaymentMethodsPage from './PaymentMethodsPage';
import TenantsPage from './TenantsPage';
import StaffPage from './StaffPage';
import ChatSupportPage from './ChatSupportPage';
import GameRecordsPage from './GameRecordsPage';
import SettingsPage from './SettingsPage';
import BannersPage from './BannersPage';
import TenantAnalyticsPage from './TenantAnalyticsPage';
import TasksRewardsPage from './TasksRewardsPage';
import TelegramConfigPage from './TelegramConfigPage';
import NotificationsPage from './NotificationsPage';
import DepositRequestsPage from './DepositRequestsPage';
import WithdrawalRequestsPage from './WithdrawalRequestsPage';
import TiersPage from './TiersPage';
import ProviderHealthPage from './ProviderHealthPage';
import TenantDashboardPage from './TenantDashboardPage';
import TenantSettingsPage from './TenantSettingsPage';
import TenantBrandingPage from './TenantBrandingPage';
import ActivityAnalyticsPage from './ActivityAnalyticsPage';
import UserAnalyticsPage from './UserAnalyticsPage';
import OperationsCenterPage from './OperationsCenterPage';
import StaffActivityPage from './StaffActivityPage';
import ReferralStaffPage from './ReferralStaffPage';
import ReferralStaffActivityPage from './ReferralStaffActivityPage';
import StaffEarningsPage from './StaffEarningsPage';
import CommissionMatrixPage from './CommissionMatrixPage';
import PhoneVerificationsPage from './PhoneVerificationsPage';
import LiveActivityPage from './LiveActivityPage';
import SegmentationPage from './SegmentationPage';
import InsightsDashboardPage from './InsightsDashboardPage';
import TenantCatalogPage from './TenantCatalogPage';
import PromoCodesPage from './PromoCodesPage';
import InstalledDevicesPage from './InstalledDevicesPage';
import TenantActivityPage from './TenantActivityPage';
import ProviderBalancePage from './ProviderBalancePage';
import TenantBroadcastPage from './TenantBroadcastPage';
import ApprovalBotsPage from './ApprovalBotsPage';
import FinanceStaffPage from './FinanceStaffPage';
import ReportEmailsPage from './ReportEmailsPage';
import ExperimentsPage from './ExperimentsPage';
import FpRulesPage from './FpRulesPage';
import IpSecurityPage from './IpSecurityPage';
import BackupPage from './BackupPage';
import PaymentAnalyticsPage from './PaymentAnalyticsPage';
import GameAnalyticsPage from './GameAnalyticsPage';
import LREEarningRulesPage from './LREEarningRulesPage';
import LREPayoutHistoryPage from './LREPayoutHistoryPage';
import SentryHealthPage from './SentryHealthPage';
import CashoutRulesAdminPage from './CashoutRulesAdminPage';
import RouletteSegmentsPage from './RouletteSegmentsPage';

const ADMIN_MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'moderator', 'support'] },
  { id: 'insights', label: 'Live Insights', icon: BarChart3, roles: ['admin', 'moderator'], perm: 'analytics:view' },
  { id: 'operations-center', label: 'Operations Center', icon: Zap, roles: ['admin', 'moderator'], perm: 'analytics:view' },
  { id: 'tenant-activity', label: 'Tenant Activity', icon: Building2, masterOnly: true, roles: ['admin'], perm: 'analytics:view' },
  { id: 'tenant-analytics', label: 'Tenant Analytics', icon: BarChart3, masterOnly: true, roles: ['admin'], perm: 'analytics:view' },
  { id: 'players', label: 'Players', icon: Users, roles: ['admin', 'moderator', 'support'], perm: 'users:view' },
  { id: 'user-analytics', label: 'User Analytics', icon: Users, roles: ['admin', 'moderator'], perm: 'analytics:view' },
  { id: 'transactions', label: 'Transactions', icon: DollarSign, roles: ['admin', 'moderator'], perm: 'transactions:view' },
  { id: 'deposit-requests', label: 'Deposit Requests', icon: DollarSign, roles: ['admin', 'moderator'], perm: 'payments:view' },
  { id: 'withdrawal-requests', label: 'Withdrawal Requests', icon: DollarSign, roles: ['admin', 'moderator'], perm: 'payments:view' },
  { id: 'games', label: 'Games', icon: Gamepad2, roles: ['admin'], perm: 'system:config' },
  { id: 'tenant-catalog', label: 'Tenant Visibility', icon: Layers, roles: ['admin'], perm: 'system:config' },
  { id: 'providers', label: 'Game Providers', icon: Server, roles: ['admin'], perm: 'system:config' },
  { id: 'provider-balance', label: 'Provider Balance', icon: Wallet, roles: ['admin'], perm: 'analytics:view' },
  { id: 'tenant-broadcast', label: 'Broadcast to Tenants', icon: Megaphone, masterOnly: true, roles: ['admin'] },
  { id: 'payments', label: 'Payments Configuration', icon: DollarSign, roles: ['admin'], perm: 'system:config' },
  { id: 'tasks-rewards', label: 'Tasks & Rewards', icon: Trophy, roles: ['admin'], perm: 'promotions:edit' },
  { id: 'roulette-segments', label: 'Roulette Wheel', icon: Disc3, roles: ['admin'], perm: 'promotions:edit' },
  { id: 'promo-codes', label: 'Promo Codes', icon: Tag, roles: ['admin'], perm: 'promotions:manage' },
  { id: 'installed-devices', label: 'Installed Devices', icon: Download, roles: ['admin'], perm: 'analytics:view' },
  { id: 'report-emails', label: 'Report Emails', icon: Mail, roles: ['admin'], perm: 'settings:edit' },
  { id: 'experiments', label: 'A/B Experiments', icon: FlaskConical, roles: ['admin'], perm: 'promotions:manage' },
  { id: 'tiers', label: 'VIP Tiers', icon: Star, roles: ['admin'], perm: 'system:config' },
  { id: 'fp-rules', label: 'FP Rules', icon: Sparkles, roles: ['admin'], perm: 'system:config' },
  { id: 'ip-security', label: 'IP Security', icon: Shield, roles: ['admin'], perm: 'system:config' },
  { id: 'backup', label: 'Atlas Backup', icon: Cloud, masterOnly: true, roles: ['admin'], perm: 'system:config' },
  { id: 'payment-analytics', label: 'Payment Analytics', icon: CreditCard, roles: ['admin', 'finance'], perm: 'reports:read' },
  { id: 'game-analytics', label: 'Game Analytics', icon: Gamepad2, roles: ['admin', 'finance'], perm: 'reports:read' },
  { id: 'banners', label: 'Announcements', icon: FileText, roles: ['admin', 'moderator'], perm: 'announcements:manage' },
  { id: 'tenants', label: 'Tenants', icon: Building2, masterOnly: true, roles: ['admin'] },
  { id: 'staff', label: 'Staff', icon: Shield, roles: ['admin'], perm: 'staff:manage' },
  { id: 'referral-staff', label: 'Referral Staff', icon: Users, roles: ['admin'], perm: 'staff:manage' },
  { id: 'referral-staff-activity', label: 'Referral Staff Activity', icon: Activity, roles: ['admin'], perm: 'staff:manage' },
  { id: 'staff-earnings', label: 'Staff Earnings', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'commissions', label: 'Commission Matrix', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'lre-rules', label: 'Referral Earning Rules', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'lre-payouts', label: 'LRE Payout History', icon: DollarSign, roles: ['admin'], perm: 'staff:manage' },
  { id: 'phone-verifications', label: 'Verify Phone Numbers', icon: Phone, roles: ['admin'], perm: 'users:manage' },
  { id: 'chat', label: 'Live Chat', icon: MessageCircle, roles: ['admin', 'moderator', 'support'], perm: 'chat:respond' },
  { id: 'notifications', label: 'Notifications', icon: Bell, roles: ['admin', 'moderator'], perm: 'notifications:broadcast' },
  { id: 'telegram', label: 'Telegram Bots', icon: Bot, roles: ['admin'], perm: 'system:config' },
  { id: 'approval-bots', label: 'Approval Bots', icon: Bot, roles: ['admin'] },
  { id: 'finance-staff', label: 'Finance Staff', icon: Shield, masterOnly: true, roles: ['admin'] },
  { id: 'game-records', label: 'Game Records', icon: BarChart3, roles: ['admin'], perm: 'analytics:view' },
  { id: 'activity', label: 'Activity Analytics', icon: Activity, roles: ['admin'], perm: 'analytics:view' },
  { id: 'staff-activity', label: 'Staff Activity', icon: Shield, roles: ['admin'], perm: 'audit:view' },
  { id: 'live-activity', label: 'Live Activity', icon: Zap, roles: ['admin'], perm: 'analytics:view' },
  { id: 'segmentation', label: 'Segmentation', icon: PieChart, roles: ['admin'], perm: 'analytics:view' },
  { id: 'provider-health', label: 'Provider Health', icon: Activity, masterOnly: true, roles: ['admin'] },
  { id: 'sentry-health', label: 'Observability — Sentry', icon: Bug, masterOnly: true, roles: ['admin'] },
  { id: 'cashout-rules', label: 'Cashout Rules', icon: ScrollText, roles: ['admin'], perm: 'system:config' },
  { id: 'settings', label: 'Settings', icon: Settings, roles: ['admin'], perm: 'system:config' },
  { id: 'branding', label: 'Branding & Customization', icon: Palette, tenantOnly: true, roles: ['admin'] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [navSearch, setNavSearch] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [myPerms, setMyPerms] = useState(null); // null = loading; array = loaded

  // Fetch my effective permissions once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/staff/my-permissions`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('session_token') || ''}` }
    })
      .then(r => r.ok ? r.json() : { permissions: [], is_master_admin: false })
      .then(d => setMyPerms({ list: d.permissions || [], master: d.is_master_admin }))
      .catch(() => setMyPerms({ list: [], master: false }));
  }, []);

  // Fetch tenant branding for tenant admins
  useEffect(() => {
    if (user?.tenant_id && !user?.is_master_admin) {
      // Get tenant via slug (tenantSlug from URL) or by id
      if (tenantSlug) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/public/${tenantSlug}`)
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setTenantInfo(d))
          .catch(() => {});
      }
    }
  }, [user?.tenant_id, user?.is_master_admin, tenantSlug]);

  useEffect(() => {
    // Parse active tab from URL
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    
    if (lastSegment && lastSegment !== 'admin' && ADMIN_MENU.some(m => m.id === lastSegment)) {
      setActiveTab(lastSegment);
    }
  }, [location]);

  const handleLogout = async () => {
    await logout();
    window.location.href = tenantSlug ? `/${tenantSlug}/auth` : '/auth';
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setMobileSidebarOpen(false);
    setNavSearch('');
    const basePath = tenantSlug ? `/${tenantSlug}/admin` : '/admin';
    navigate(tabId === 'dashboard' ? basePath : `${basePath}/${tabId}`);
  };

  const isTenantAdmin = !user?.is_master_admin && !!user?.tenant_id;
  const isRoleAdmin = user?.role === 'admin' || user?.is_master_admin;

  const filteredMenu = ADMIN_MENU.filter(item => {
    if (!item.roles.includes(user?.role)) return false;
    if (item.masterOnly && !user?.is_master_admin) return false;
    if (item.tenantOnly && !isTenantAdmin) return false;
    // Permission filter: role=admin gets everything; role=moderator/support must have the permission
    if (item.perm && !isRoleAdmin) {
      if (!myPerms) return true; // show while loading to avoid flicker
      if (!myPerms.list.includes(item.perm)) return false;
    }
    return true;
  });

  const normalizeSearch = (value) => value.trim().toLowerCase();
  const navQuery = normalizeSearch(navSearch);
  const searchedMenu = navQuery
    ? filteredMenu.filter(item => {
        const haystack = `${item.label} ${item.id}`.replace(/-/g, ' ').toLowerCase();
        return haystack.includes(navQuery);
      })
    : filteredMenu;
  const canQuickOpen = navQuery && searchedMenu.length > 0;

  const handleNavSearchKeyDown = (e) => {
    if (e.key === 'Enter' && canQuickOpen) {
      e.preventDefault();
      handleTabChange(searchedMenu[0].id);
    }
    if (e.key === 'Escape') {
      setNavSearch('');
    }
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'dashboard': return isTenantAdmin ? <TenantDashboardPage onNavigate={handleTabChange} /> : <DashboardPage />;
      case 'insights': return <InsightsDashboardPage />;
      case 'operations-center': return <OperationsCenterPage />;
      case 'tenant-activity': return <TenantActivityPage />;
      case 'players': return <PlayersPage />;
      case 'user-analytics': return <UserAnalyticsPage />;
      case 'transactions': return <TransactionsPage />;
      case 'games': return <GamesPage />;
      case 'tenant-catalog': return <TenantCatalogPage />;
      case 'providers': return <ProvidersPage />;
      case 'provider-balance': return <ProviderBalancePage />;
      case 'tenant-broadcast': return <TenantBroadcastPage />;
      case 'payments': return <PaymentMethodsPage />;
      case 'tenants': return <TenantsPage />;
      case 'tenant-analytics': return <TenantAnalyticsPage />;
      case 'staff': return <StaffPage />;
      case 'chat': return <ChatSupportPage />;
      case 'notifications': return <NotificationsPage />;
      case 'game-records': return <GameRecordsPage />;
      case 'settings': return isTenantAdmin ? <TenantSettingsPage /> : <SettingsPage />;
      case 'branding': return <TenantBrandingPage />;
      case 'banners': return <BannersPage />;
      case 'tasks-rewards': return <TasksRewardsPage />;
      case 'roulette-segments': return <RouletteSegmentsPage />;
      case 'telegram': return <TelegramConfigPage />;
      case 'approval-bots': return <ApprovalBotsPage />;
      case 'finance-staff': return <FinanceStaffPage />;
      case 'deposit-requests': return <DepositRequestsPage />;
      case 'withdrawal-requests': return <WithdrawalRequestsPage />;
      case 'tiers': return <TiersPage />;
      case 'fp-rules': return <FpRulesPage />;
      case 'ip-security': return <IpSecurityPage />;
      case 'backup': return <BackupPage />;
      case 'payment-analytics': return <PaymentAnalyticsPage />;
      case 'game-analytics': return <GameAnalyticsPage />;
      case 'provider-health': return <ProviderHealthPage />;
      case 'sentry-health': return <SentryHealthPage />;
      case 'cashout-rules': return <CashoutRulesAdminPage />;
      case 'activity': return <ActivityAnalyticsPage />;
      case 'staff-activity': return <StaffActivityPage />;
      case 'referral-staff': return <ReferralStaffPage />;
      case 'referral-staff-activity': return <ReferralStaffActivityPage />;
      case 'staff-earnings': return <StaffEarningsPage />;
      case 'commissions': return <CommissionMatrixPage />;
      case 'lre-rules': return <LREEarningRulesPage />;
      case 'lre-payouts': return <LREPayoutHistoryPage />;
      case 'phone-verifications': return <PhoneVerificationsPage />;
      case 'promo-codes': return <PromoCodesPage />;
      case 'installed-devices': return <InstalledDevicesPage />;
      case 'report-emails': return <ReportEmailsPage />;
      case 'experiments': return <ExperimentsPage />;
      case 'live-activity': return <LiveActivityPage />;
      case 'segmentation': return <SegmentationPage />;
      default: return isTenantAdmin ? <TenantDashboardPage onNavigate={handleTabChange} /> : <DashboardPage />;
    }
  };

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: '#0B0E14' }} data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen shrink-0 bg-[#131822] border-r border-white/5 transition-transform duration-200 flex flex-col ${
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {isTenantAdmin && tenantInfo ? (
              <>
                {tenantInfo.branding?.logo_url ? (
                  <img src={tenantInfo.branding.logo_url.startsWith('http') ? tenantInfo.branding.logo_url : `${process.env.REACT_APP_BACKEND_URL}${tenantInfo.branding.logo_url}`}
                    alt={tenantInfo.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" data-testid="admin-tenant-logo" />
                ) : (
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${tenantInfo.branding?.primary_color || '#D4AF37'}, ${tenantInfo.branding?.secondary_color || '#F5D76E'})` }}>
                    <span className="text-sm font-bold text-black">{(tenantInfo.name || 'T').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <span className="font-bold truncate block text-sm" style={{ color: tenantInfo.branding?.primary_color || '#D4AF37' }} data-testid="admin-tenant-name">{tenantInfo.name}</span>
                  <p className="text-[10px] text-gray-400 capitalize">{user?.role?.replace('_', ' ')} · Tenant Admin</p>
                </div>
              </>
            ) : (
              <>
                <img src="/vegas-lobby-logo.png" alt="Vegas Lobby" className="h-9 w-auto flex-shrink-0" />
                <div className="min-w-0">
                  <span className="font-bold gold-text">Vegas Lobby</span>
                  <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')} {user?.is_master_admin ? '· Master' : ''}</p>
                </div>
              </>
            )}
          </div>
          <button onClick={() => setMobileSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <div className="px-4 pt-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              onKeyDown={handleNavSearchKeyDown}
              placeholder="Search admin menu..."
              className="pl-9 pr-10 bg-[#0B0E14] border-white/10 h-9 text-sm"
              data-testid="admin-nav-search"
            />
            {navSearch && (
              <button
                type="button"
                onClick={() => setNavSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded text-[11px] text-gray-500 hover:text-white hover:bg-white/5"
                aria-label="Clear admin menu search"
              >
                Clear
              </button>
            )}
          </div>
          {navQuery && (
            <p className="mt-2 text-[11px] text-gray-500" data-testid="admin-nav-search-count">
              {searchedMenu.length ? `${searchedMenu.length} section${searchedMenu.length === 1 ? '' : 's'} found` : 'No matching admin sections'}
            </p>
          )}
        </div>
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto scrollbar-thin">
          {searchedMenu.map(item => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`sidebar-item w-full ${activeTab === item.id ? 'active' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          ))}
          {navQuery && searchedMenu.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-gray-500 border border-dashed border-white/10 rounded-xl">
              No menu item matches "{navSearch}".
            </div>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-white/5 flex-shrink-0 space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold">
              {user?.full_name?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 text-sm font-semibold transition"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-4 lg:px-6 border-b border-white/5 bg-[#131822] flex-shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/5">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold capitalize">
              {(filteredMenu.find(m => m.id === activeTab)?.label) || activeTab.replace(/-/g, ' ')}
            </h1>
            {isTenantAdmin && tenantInfo && (
              <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{ background: `${tenantInfo.branding?.primary_color || '#D4AF37'}1a`, color: tenantInfo.branding?.primary_color || '#D4AF37', border: `1px solid ${tenantInfo.branding?.primary_color || '#D4AF37'}40` }}
                data-testid="admin-tenant-badge">
                {tenantInfo.name}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={navSearch}
                onChange={(e) => setNavSearch(e.target.value)}
                onKeyDown={handleNavSearchKeyDown}
                placeholder="Search admin..."
                className="pl-9 w-56 bg-[#0B0E14] border-white/10 h-9"
                data-testid="admin-header-search"
              />
              {canQuickOpen && (
                <div className="absolute right-0 top-11 w-72 rounded-xl border border-white/10 bg-[#131822] shadow-2xl overflow-hidden z-50" data-testid="admin-header-search-results">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 border-b border-white/5">
                    Press Enter to open first result
                  </div>
                  {searchedMenu.slice(0, 6).map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-white/5"
                      data-testid={`admin-search-result-${item.id}`}
                    >
                      <item.icon className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-gray-100">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="p-2 rounded-lg hover:bg-white/5 relative">
              <Bell className="w-5 h-5 text-gray-400" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 text-xs font-semibold transition"
              data-testid="admin-header-logout-btn"
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto" style={{ contain: 'none' }}>
          {renderPage()}
        </main>
      </div>
    </div>
  );
}
